/**
 * PhotoReportConfigForm — shared settings form for Photo Report Templates.
 * Used in Settings (organisation defaults) and the Checklist Builder
 * (per-checklist override). Controlled: value + onChange(partial).
 */
import { ANNOT_COLORS } from "./AnnotationEditor";

const LAYOUTS = [
  ["one_per_page", "One photo per page"],
  ["two_per_page", "Two photos per page"],
  ["notes_under", "Flowing — notes underneath"],
  ["side_by_side", "Photo left, notes right"],
];

const COLOR_NAMES = { "#EF4444": "Red", "#FACC15": "Yellow", "#3B82F6": "Blue", "#22C55E": "Green", "#FFFFFF": "White" };

async function fileToLogo(file) {
  const dataUrl = await new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = dataUrl;
  });
  const scale = Math.min(1, 400 / img.naturalWidth);
  const c = document.createElement("canvas");
  c.width = Math.round(img.naturalWidth * scale);
  c.height = Math.round(img.naturalHeight * scale);
  c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
  return c.toDataURL("image/png");
}

export const PhotoReportConfigForm = ({ value = {}, onChange, showEnabled = false, testid = "pr-config" }) => {
  const colors = value.colors || ANNOT_COLORS;
  const toggleColor = (c) => {
    const next = colors.includes(c) ? colors.filter((x) => x !== c) : [...colors, c];
    if (next.length === 0) return; // keep at least one colour
    onChange({ colors: next });
  };
  return (
    <div className="space-y-4" data-testid={testid}>
      {showEnabled && (
        <label className="flex items-center gap-2.5 cursor-pointer">
          <input type="checkbox" checked={value.enabled !== false}
            onChange={(e) => onChange({ enabled: e.target.checked })}
            className="h-5 w-5 accent-[#00F2C3]" data-testid={`${testid}-enabled`}/>
          <span className="text-sm font-bold text-slate-900">Enable Photo Report Template for this checklist</span>
        </label>
      )}
      <label className="block">
        <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Default report title</span>
        <input value={value.default_title || ""} onChange={(e) => onChange({ default_title: e.target.value })}
          placeholder="e.g. Pool Barrier Photo Report" data-testid={`${testid}-title`}
          className="w-full h-11 px-3 border-2 border-slate-200 focus:border-[#1E1B4B] focus:outline-none text-sm"/>
        <span className="text-[10px] text-slate-400">The property address is added automatically after the title.</span>
      </label>
      <label className="block">
        <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Default page layout</span>
        <select value={value.default_layout || "one_per_page"} onChange={(e) => onChange({ default_layout: e.target.value })}
          data-testid={`${testid}-layout`}
          className="w-full h-11 px-2 border-2 border-slate-200 bg-white text-xs font-bold text-slate-700">
          {LAYOUTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </label>
      <div>
        <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Report logo</span>
        <div className="flex items-center gap-3 mt-1">
          {value.logo ? (
            <>
              <img src={value.logo} alt="Photo report logo" className="h-12 max-w-[180px] object-contain bg-white p-1 border border-slate-200" data-testid={`${testid}-logo-preview`}/>
              <button onClick={() => onChange({ logo: "" })} data-testid={`${testid}-logo-clear`}
                className="h-9 px-3 border-2 border-slate-200 bg-white text-xs font-bold text-slate-600 hover:border-red-400 hover:text-red-700">Remove</button>
            </>
          ) : (
            <span className="text-xs text-slate-500">Using your business branding logo</span>
          )}
          <label className="h-9 px-3 inline-flex items-center border-2 border-slate-200 bg-white text-xs font-bold text-slate-700 hover:border-slate-900 cursor-pointer">
            Upload logo
            <input type="file" accept="image/*" className="hidden" data-testid={`${testid}-logo-input`}
              onChange={async (e) => {
                const f = e.target.files?.[0];
                if (f) onChange({ logo: await fileToLogo(f) });
                e.target.value = "";
              }}/>
          </label>
        </div>
      </div>
      <div>
        <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Annotation colours available to officers</span>
        <div className="flex items-center gap-2 mt-1.5">
          {ANNOT_COLORS.map((c) => (
            <button key={c} title={COLOR_NAMES[c] || c} onClick={() => toggleColor(c)}
              data-testid={`${testid}-color-${c.replace("#", "")}`}
              className={`h-9 w-9 border-2 relative ${colors.includes(c) ? "border-[#1E1B4B]" : "border-slate-200 opacity-30"}`}
              style={{ backgroundColor: c }}>
              {colors.includes(c) && <span className="absolute -top-1.5 -right-1.5 h-4 w-4 bg-[#00F2C3] text-[#062A2E] text-[9px] font-black flex items-center justify-center rounded-full">✓</span>}
            </button>
          ))}
        </div>
      </div>
      <label className="flex items-center gap-2.5 cursor-pointer">
        <input type="checkbox" checked={!!value.notes_required}
          onChange={(e) => onChange({ notes_required: e.target.checked })}
          className="h-5 w-5 accent-[#00F2C3]" data-testid={`${testid}-notes-required`}/>
        <span className="text-sm text-slate-800"><b>Notes are mandatory</b> — every photo must have officer notes before the PDF can be generated</span>
      </label>
    </div>
  );
};

export default PhotoReportConfigForm;
